'use client';

import { LandingLayout } from '@/features/landing/LandingLayout';
import { Search, BookOpen, MessageCircle, Video, FileText, Zap, ChevronDown } from 'lucide-react';
import { useState } from 'react';

const categories = [
  {
    icon: Zap,
    title: 'Getting Started',
    description: 'Set up your workspace, invite your team, and configure your first quality evaluation.',
    articles: [
      {
        title: 'Quick start guide',
        content: 'Welcome to Swishr! To get started, log in to your account and complete the onboarding wizard. You\'ll be prompted to set up your organization profile, invite team members, and connect your first data source. The whole process takes under 10 minutes.',
      },
      {
        title: 'Connecting your data sources',
        content: 'Swishr supports integrations with major CRM, telephony, and ticketing platforms. Go to Settings → Integrations to connect your tools. Once connected, interactions will automatically sync and become available for evaluation and analysis.',
      },
      {
        title: 'Setting up your first scorecard',
        content: 'Navigate to Quality → Templates and click "New Template". Add your evaluation parameters, assign weights to each section, and save. Your scorecard is now ready to use when evaluating agent interactions.',
      },
    ],
  },
  {
    icon: FileText,
    title: 'Quality Management',
    description: 'Learn how to build evaluation templates, score interactions, and track agent performance.',
    articles: [
      {
        title: 'Creating evaluation templates',
        content: 'Go to Settings → Quality to create and manage your evaluation templates. Each template can have multiple sections (e.g. Greeting, Problem Resolution, Compliance) with individual parameters and weights. Templates can be duplicated and customised for different teams or channels.',
      },
      {
        title: 'Scoring calls & chats',
        content: 'From the Quality module, click "New Evaluation" and select the interaction you want to score. Choose your template, go through each parameter, and submit. Scores are automatically calculated and added to the agent\'s performance record.',
      },
      {
        title: 'Using AI-assisted scoring',
        content: 'Enable AI scoring in Settings → Quality → AI Scoring. Once enabled, Swishr will automatically analyse 100% of interactions and pre-fill evaluation scores. Reviewers can then verify, adjust, and approve AI-generated scores — saving hours of manual QA work.',
      },
    ],
  },
  {
    icon: BookOpen,
    title: 'WFM & Scheduling',
    description: 'Master workforce forecasting, schedule generation, and adherence tracking.',
    articles: [
      {
        title: 'Uploading historical data',
        content: 'Go to WFM → Models and click "New Model". Upload your historical volume data in CSV format. Swishr accepts interval-level data (15 or 30 minute increments). The more historical data you provide, the more accurate your forecasts will be.',
      },
      {
        title: 'Generating schedules',
        content: 'Once your forecast model is ready, go to Scheduling → Generate. Select your forecast, set your team size and shift constraints, and click Generate. Swishr will produce an optimised schedule in seconds that you can review, edit, and publish.',
      },
      {
        title: 'Tracking adherence in real-time',
        content: 'The Adherence dashboard shows you live schedule adherence for all agents. Green means on schedule, yellow means slightly off, red means significantly out of adherence. Click any agent to see their full schedule vs. actual activity timeline.',
      },
    ],
  },
  {
    icon: MessageCircle,
    title: 'DSAT & Insights',
    description: 'Understand dissatisfied customer signals and take action with AI-driven recommendations.',
    articles: [
      {
        title: 'What is DSAT scoring',
        content: 'DSAT (Dissatisfaction) scoring in Swishr identifies interactions where customers were likely dissatisfied, even without explicit feedback. Our AI analyses conversation signals, tone, resolution patterns, and escalation indicators to generate a DSAT probability score for every interaction.',
      },
      {
        title: 'Root cause analysis',
        content: 'Go to DSAT → Root Causes to see an AI-generated breakdown of why customers are dissatisfied. Causes are clustered by theme (e.g. "Long Hold Times", "Unresolved Issues") and ranked by frequency and impact, so you know exactly where to focus improvement efforts.',
      },
      {
        title: 'Creating action plans',
        content: 'From any DSAT record, click "Create Action Plan" to assign corrective actions to team leaders or agents. Set due dates, add notes, and track completion. Action plans are linked to the original DSAT record so you can measure whether the intervention made a difference.',
      },
    ],
  },
  {
    icon: Video,
    title: 'Coaching & Quizzes',
    description: 'Create coaching plans, assign quizzes, and track agent learning progress.',
    articles: [
      {
        title: 'Building a coaching plan',
        content: 'Go to Coaching → New Plan. Select the agent, choose a focus area (e.g. empathy, compliance, product knowledge), and set a target metric. Swishr will suggest coaching actions based on recent evaluation data. Assign tasks, set a due date, and track progress from the coaching dashboard.',
      },
      {
        title: 'Creating quiz templates',
        content: 'Go to Settings → Quiz to create reusable question banks. Add multiple choice, true/false, or open-ended questions. Organise questions by topic and difficulty. Once your question bank is ready, you can build quizzes by pulling questions from it.',
      },
      {
        title: 'Tracking completion rates',
        content: 'The Quiz Analytics dashboard shows completion rates, average scores, and time-to-complete by quiz, team, and individual agent. Use this data to identify knowledge gaps and target coaching efforts where they are needed most.',
      },
    ],
  },
  {
    icon: Search,
    title: 'Integrations',
    description: 'Connect Swishr to your existing CRM, telephony, and ticketing platforms.',
    articles: [
      {
        title: 'Supported integrations',
        content: 'Swishr currently supports integrations with Zendesk, Salesforce, Freshdesk, Genesys, Avaya, and Amazon Connect. Additional integrations are available via our REST API and webhook system. Visit Settings → Integrations to see the full list and connection guides.',
      },
      {
        title: 'Zendesk setup guide',
        content: 'Go to Settings → Integrations → Zendesk. Enter your Zendesk subdomain and generate an API token from your Zendesk admin panel. Paste it into Swishr and click Connect. Ticket data will begin syncing within minutes. You can configure which ticket fields to import and map them to Swishr\'s data model.',
      },
      {
        title: 'API-based integrations',
        content: 'For platforms not natively supported, use the Swishr REST API to push interaction data directly. Generate an API key from Settings → API, then use the POST /v1/interactions endpoint to send interaction records. See our API Reference for full documentation.',
      },
    ],
  },
];

function ArticleAccordion({ title, content }: { title: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/50 transition-colors"
      >
        <span className="text-sm font-medium text-foreground">{title}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-border bg-muted/20">
          <p className="text-sm text-muted-foreground leading-relaxed">{content}</p>
        </div>
      )}
    </div>
  );
}

export default function HelpCenterPage() {
  const [search, setSearch] = useState('');

  const filtered = categories.map((cat) => ({
    ...cat,
    articles: cat.articles.filter(
      (a) =>
        search === '' ||
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        a.content.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((cat) => search === '' || cat.articles.length > 0);

  return (
    <LandingLayout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-20 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-14">
          <h1 className="text-4xl font-bold text-foreground mb-4">Help Center</h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
            Find answers, guides, and resources to get the most out of Swishr.
          </p>
          {/* Search */}
          <div className="relative max-w-lg mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {/* Categories */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">No articles found for &quot;{search}&quot;.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filtered.map((cat) => {
              const Icon = cat.icon;
              return (
                <div key={cat.title} className="border border-border rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground">{cat.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{cat.description}</p>
                  <div className="space-y-2">
                    {cat.articles.map((article) => (
                      <ArticleAccordion key={article.title} title={article.title} content={article.content} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Contact Support */}
        <div className="border border-border rounded-2xl p-8 text-center mt-16">
          <h2 className="text-xl font-semibold text-foreground mb-2">Still need help?</h2>
          <p className="text-muted-foreground mb-6">Our support team is available 24/5 to assist you.</p>
          <a
            href="mailto:support@swishr.ai"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    </LandingLayout>
  );
}
